import { useCallback, useState } from "react";
import { useAuth } from "@clerk/clerk-react";

/**
 * Whether to greet the workspace with the brand-setup dialog, and how to close
 * it for good.
 *
 * Who sees it is one rule, not two: a workspace that has not saved a brand yet.
 * That covers the account created a moment ago by the sign-up flow, and equally
 * the one signing back in a week later having skipped it the first time — the
 * dialog is about the step being outstanding, not about how they arrived.
 *
 * Once per sign-in, and that is the whole of the bookkeeping. It opens as the
 * dashboard lands, and once it has been answered — either way — it stays shut
 * for the rest of that session, so walking off to My clients and back does not
 * put it up again. A fresh sign-in mints a new Clerk session id and earns one
 * more showing, which is the most a first-run prompt should ever ask for. The
 * day the brand is saved the condition goes away on its own and it is never
 * seen again.
 *
 * Keyed on the Clerk session id in `localStorage`, the same way
 * `useSessionBootstrap` remembers its handshake — a page reload is the same
 * sign-in and must not be a second showing.
 *
 * Must be used inside a Clerk provider.
 */

export interface BrandWelcome {
  /** Whether the dialog belongs on screen right now. */
  open: boolean;
  /** Closes it, and marks this session as having been asked. */
  dismiss: () => void;
}

/** Single key, holding the last session id that was shown the dialog. */
const SEEN_SESSION_KEY = "naurist:brand-welcome-session";

/**
 * Mirror of the stored value, read from storage once per page load, so an
 * already-greeted session is recognised during render rather than in an effect
 * — otherwise the dialog would flash up for a frame on every reload.
 */
let seenSession: string | null | undefined;

// Storage can throw outright — Safari private mode, or a browser with site data
// blocked. A session that cannot be remembered is greeted again next load,
// which is a far better failure than the dashboard not rendering.
const readSeenSession = (): string | null => {
  if (seenSession === undefined) {
    try {
      seenSession = window.localStorage.getItem(SEEN_SESSION_KEY);
    } catch {
      seenSession = null;
    }
  }
  return seenSession;
};

const rememberSeenSession = (sessionId: string): void => {
  seenSession = sessionId;
  try {
    window.localStorage.setItem(SEEN_SESSION_KEY, sessionId);
  } catch {
    /* not fatal — see above */
  }
};

/**
 * @param brandPending Whether the workspace is known to have no brand saved.
 *   Pass it only once the answer is actually in: a brand read that failed is
 *   not a brand that is missing, and greeting someone who set theirs up last
 *   month because a request timed out is worse than staying quiet.
 */
export function useBrandWelcome(brandPending: boolean): BrandWelcome {
  const { sessionId } = useAuth();

  // Dismissal within this page's life. The stored id covers reloads; this
  // covers the ordinary case of closing it and carrying on, and is also what
  // closes the dialog where storage is unavailable.
  const [dismissed, setDismissed] = useState(false);

  const dismiss = useCallback(() => {
    setDismissed(true);
    if (sessionId) rememberSeenSession(sessionId);
  }, [sessionId]);

  // No session id yet means Clerk is still restoring, and there is nothing to
  // remember a showing against — so nothing is shown until there is.
  const open =
    brandPending &&
    !!sessionId &&
    !dismissed &&
    readSeenSession() !== sessionId;

  return { open, dismiss };
}

export default useBrandWelcome;
